/** Look up the villain's probability of CONTINUING (calling/betting) with a class. Missing key → 0.5. */
export const continueProbFor = (arch, hc) => clamp01(arch.postflop.continueProb[hc] ?? 0.5);
/** Look up the villain's probability of RAISING with a class. Missing key → 0.05. */
export const raiseProbFor = (arch, hc) => clamp01(arch.postflop.raiseProb[hc] ?? 0.05);
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
/**
 * How much "pressure" a faced event applies, ≈ bet size as a pot fraction proxy. 1.0 = a normal
 * half/two-thirds bet; >1 = large/overbet/jam; <1 = a small stab or a check. Only the pressure
 * ABOVE/below 1 matters - it feeds the elasticity curve.
 */
const facingPressure = (facing) => {
    switch (facing) {
        case 'check': return 0.0; // no chips in - nothing to fold to
        case 'cbet-small':
        case 'bet-small':
        case 'blockerBet':
            return 0.75;
        case 'delayedCbet':
        case 'donk':
            return 1.0;
        case 'cbet-large':
        case 'bet-large':
        case 'raise':
        case 'donk-large':
            return 1.3;
        case 'checkRaise':
            return 1.5;
        case 'overbet':
            return 1.7;
        case 'riverJam':
            return 2.0;
        default:
            return 1.0;
    }
};
/**
 * Adjust a base continue probability for how price-sensitive this archetype is at this bet size.
 *   inelastic (stations, scared-money short jams): pressure barely changes the base - they call/fold
 *              on hand strength, not price. Multiplier ≈ 1.
 *   elastic:   larger bets fold out more of the range: base ^ pressure (pressure>1 shrinks, <1 grows).
 *   normal:    a mild version of elastic.
 * Pressure 1.0 leaves every profile at its base (a "standard" bet is the calibration point).
 *
 * Exported so the S9 scenario engine's villain scripting samples fold/call/raise through the SAME
 * price-sensitivity curve that narrowing conditions on - the villain literally acts from the elasticity
 * the coach reasons about (§4.3's single-source design), rather than a second, driftable copy.
 */
export const applyElasticity = (base, elasticity, pressure) => {
    if (pressure === 1.0 || base <= 0)
        return clamp01(base);
    const exponent = elasticity === 'inelastic' ? 1 + (pressure - 1) * 0.15 // barely moves
        : elasticity === 'elastic' ? 1 + (pressure - 1) * 1.0 // full price sensitivity
            : 1 + (pressure - 1) * 0.5; // normal
    return clamp01(Math.pow(base, exponent));
};
/**
 * The weight multiplier a combo of class `hc` keeps after facing `evt`, for archetype `arch`.
 * Never returns >1 for a continue/raise event, so narrowing only ever removes mass (§4.2:
 * "weight multiplication, never deletion"). Always ≥ 0.
 */
export const narrowMultiplier = (arch, evt, hc) => {
    const pressure = facingPressure(evt.facing);
    switch (evt.responded) {
        case 'raise': {
            // The villain raised: only the classes they raise survive strongly.
            return applyElasticity(raiseProbFor(arch, hc), arch.imbalances.elasticity, Math.max(pressure, 1));
        }
        case 'check': {
            // The villain checked: the range that DIDN'T bet/raise. Strong classes that would have bet are
            // down-weighted; slowplay leakage is left in via the (1 − raiseProb) floor.
            return clamp01(1 - raiseProbFor(arch, hc));
        }
        case 'call':
        case 'bet':
        default: {
            // The villain called/led: the range that continues, sized by how price-sensitive they are.
            return applyElasticity(continueProbFor(arch, hc), arch.imbalances.elasticity, pressure);
        }
    }
};
/** The archetype's raw ActionModel (for consumers that want the table directly, e.g. S9). */
export const actionModelOf = (arch) => arch.postflop;
//# sourceMappingURL=action-model.js.map